import { isNumeric } from '@utils/number';
import { Calculation, CalculationType, GridDashboardChartItem } from '../../../../../types/insight-custom-dashboard';
import { tryCalculation } from '../../../../../utils/formula';
import NumberFormat from '../../../../../utils/number-format';
import { getSparklineChartProps } from '../../../../summary/insights/utils/helpers';
import { Note } from '../common/Note';
import { TakenFromText } from '../common/TakenFromText';
import { LineChart } from '../LineChart';
import { getLatestActualUtrvs, getSumOfVariables, getVariablesWithValues } from './utils';
import { ChartUtrData, DataProps } from '../../types';
import { getLineChartData } from './data-utils';

interface Props extends Pick<GridDashboardChartItem, 'variables' | 'unitText' | 'note'> {
  utrsData: ChartUtrData[];
  hasSparkLine?: boolean;
  metricUnit: string;
  decimalPlaces?: number;
  isSuffix: boolean;
  isCurrency: boolean;
  calculation: Calculation;
}

const getChartData = ({
  utrsData,
  variables,
  calculation,
}: DataProps<ChartUtrData>) => {
  const { latestActualUtrvs = [], effectiveDate, period } = getLatestActualUtrvs(utrsData);

  const variablesWithValues = getVariablesWithValues({ utrsData, variables, utrvs: latestActualUtrvs, fallback: '' });

  let chartData: string | number = '';
  switch (calculation.type) {
    case CalculationType.Sum: {
      chartData = getSumOfVariables(variablesWithValues);
      break;
    }
    default: {
      chartData = tryCalculation({
        formula: calculation.values[0].formula || '',
        variables: variablesWithValues,
        fallback: '', // NOTE: Using zero value as fallback may cause confusion with actual zero value?
      });
    }
  }

  return { chartData, effectiveDate, period, variablesWithValues };
};

export const MultiUtrsSingleValue = (props: Props) => {
  const { utrsData, variables, unitText, calculation, note, metricUnit, isSuffix, isCurrency, hasSparkLine, decimalPlaces } = props;

  const {
    chartData,
    effectiveDate = '',
    period,
    variablesWithValues,
  } = getChartData({ utrsData, variables, calculation });

  const showUnitBelow = !isSuffix && !isCurrency;

  return (
    <>
      <div className='w-100 h-100 d-flex flex-column justify-content-center'>
        {isNumeric(chartData) ? (
          <NumberFormat
            className='h1 m-0 text-center text-ThemeHeadingLight'
            value={chartData}
            prefix={isCurrency && !isSuffix ? metricUnit : ''}
            suffix={isSuffix ? metricUnit : ''}
            decimalPlaces={decimalPlaces}
          />
        ) : (
          <div className='h1 m-0 text-center text-ThemeHeadingLight'>-</div>
        )}
        {showUnitBelow ? <div className='text-sm text-center text-ThemeTextLight'>{metricUnit}</div> : null}
        {note ? (
          <Note note={note} variablesWithValues={variablesWithValues} className='text-center text-ThemeTextLight' />
        ) : null}
      </div>
      {hasSparkLine ? (
        <LineChart
          getChartData={getLineChartData}
          utrsData={utrsData}
          variables={variables}
          calculation={calculation}
          unitText={unitText}
          overrideOptions={getSparklineChartProps({ isRestated: false, isMultiple: true })}
          currencyConfig={undefined}
        />
      ) : null}
      {effectiveDate ? <TakenFromText effectiveDate={effectiveDate} period={period} /> : null}
    </>
  );
};
